import React from 'react';

interface TextFieldProps {
  id?: string;
  label?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | boolean | React.ReactNode;
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  className?: string;
  sx?: React.CSSProperties;
  slotProps?: {
    input?: {
      endAdornment?: React.ReactNode;
      [key: string]: any;
    };
    [key: string]: any;
  };
  InputProps?: {
    endAdornment?: React.ReactNode;
    [key: string]: any;
  };
  inputProps?: {
    min?: string;
    [key: string]: any;
  };
  FormHelperTextProps?: {
    style?: React.CSSProperties;
    [key: string]: any;
  };
}

const TextField: React.FC<TextFieldProps> = ({
  id,
  label,
  variant = 'outlined',
  size = 'medium',
  value,
  onChange,
  onFocus,
  type = 'text',
  placeholder,
  disabled = false,
  error = false,
  helperText,
  fullWidth = false,
  multiline = false,
  rows,
  className = '',
  sx,
  slotProps,
  InputProps,
  inputProps,
}) => {
  const baseClasses = 'block w-full rounded border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0';
  
  const sizeClasses = {
    small: 'px-2 py-1 text-sm',
    medium: 'px-3 py-2 text-base',
  };

  const variantClasses = {
    outlined: error 
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500',
    filled: error
      ? 'bg-red-50 dark:bg-red-900/20 border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500',
    standard: error
      ? 'border-b-2 border-red-500 focus:border-red-500'
      : 'border-b-2 border-gray-300 dark:border-gray-600 focus:border-blue-500',
  };

  const classes = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100'} ${fullWidth ? 'w-full' : ''} ${className}`;

  const InputComponent = multiline ? 'textarea' : 'input';

  const endAdornment = slotProps?.input?.endAdornment || InputProps?.endAdornment;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} relative`}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <InputComponent
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled}
          rows={multiline ? rows : undefined}
          className={classes}
          style={sx}
          min={inputProps?.min}
          {...(inputProps || {})}
        />
        {endAdornment && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {endAdornment}
          </div>
        )}
      </div>
      {helperText && typeof helperText !== 'boolean' && (
        <p className={`mt-1 text-sm ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {typeof helperText === 'string' ? helperText : helperText}
        </p>
      )}
    </div>
  );
};

export default TextField;

